import dayjs from 'dayjs';
import { z } from 'zod';
import { LicenseTypes } from '../../../types/licenses';
import { BasicBusinessInformationSchema, type BusinessInformation } from './types/businessInformation';
import { ConcertPayload } from './types/payloads';

export const musicSources = ['liveMusic', 'recordedMusic'] as const;

export const CONCERT_AND_EVENT_CONTENT_PATH = 'licenses.steps.businessInformation.forms.concertAndEvent';

export const concertAndEventBusinessInformationSchema = BasicBusinessInformationSchema.extend({
  date: z
    .string()
    .min(1, { message: `${CONCERT_AND_EVENT_CONTENT_PATH}.date.validation.required` })
    // Basic ISO date format check (YYYY-MM-DD)
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
      message: `${CONCERT_AND_EVENT_CONTENT_PATH}.date.validation.validFormat`,
    }),
  artist: z.string().min(1, { message: 'licenses.components.input.artistInput.validation.required' }),
  artistName: z.string().min(1, { message: 'licenses.components.input.artistInput.validation.required' }),
  // Store raw string so placeholder & formatting logic can work; validate positive integer or decimal.
  revenue: z
    .number({
      required_error: `${CONCERT_AND_EVENT_CONTENT_PATH}.revenue.validation.required`,
    })
    .min(0, { message: `${CONCERT_AND_EVENT_CONTENT_PATH}.revenue.validation.required` }),
  attendees: z
    .number({
      required_error: `${CONCERT_AND_EVENT_CONTENT_PATH}.attendees.validation.required`,
    })
    .int({ message: `${CONCERT_AND_EVENT_CONTENT_PATH}.attendees.validation.required` })
    .min(1, { message: `${CONCERT_AND_EVENT_CONTENT_PATH}.attendees.validation.required` }),
  primaryMusicSource: z.enum(musicSources),
});

export type ConcertAndEventForm = z.infer<typeof concertAndEventBusinessInformationSchema>;

export const createConcertAndEventDefaults = (): BusinessInformation => ({
  licenseCode: LicenseTypes.ConcertAndEvent,
  date: '',
  artist: '',
  artistName: '',
  primaryMusicSource: 'liveMusic',
});

export const toConcertPayload = (form: ConcertAndEventForm): ConcertPayload => {
  return {
    musicStartDate: dayjs(form.date).format('YYYY-MM-DD'),
    quantity: form.attendees ?? 0,
    basis: form.revenue ?? 0,
    isLiveMusic: form.primaryMusicSource !== 'recordedMusic',
  };
};
